import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import DescriptionIcon from "@mui/icons-material/Description";
import SendIcon from "@mui/icons-material/Send";

export const MisIntervencionesFinalizadas = () => {
  const { auth } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [ticajes, setTicajes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // Filtros
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroOrden, setFiltroOrden] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState("");
  const [busquedaTexto, setBusquedaTexto] = useState("");

  // UI
  const [cargando, setCargando] = useState(true);

  // DataGrid paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Orden controlada
  const [orden, setOrden] = useState({ campo: "inicio", ascendente: true });

  // ==========================
  // ✉️ Envío por email (modal)
  // ==========================
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicaje, setSelectedTicaje] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  const openSendModal = useCallback((row) => {
    setSelectedTicaje(row || null);

    const prefill =
      (row?.cliente_email || row?.email_cliente || row?.email || "").toString().trim();

    setEmailTo(prefill);
    setModalOpen(true);
  }, []);

  const closeSendModal = () => {
    if (sending) return;
    setModalOpen(false);
    setSelectedTicaje(null);
    setEmailTo("");
  };

  const isEmailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

  const enviarParteEmail = async () => {
    const ticajeId = selectedTicaje?.id;
    const email = String(emailTo || "").trim();

    if (!ticajeId) {
      toast.error("No se pudo determinar el ticaje.");
      return;
    }
    if (!isEmailValid(email)) {
      toast.error("Email inválido.");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${Global.url}partes-trabajo/ticaje-finalizado/${ticajeId}/enviar-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        let msg = txt || "No se pudo enviar el parte.";
        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      toast.success("Parte enviado por email.");
      closeSendModal();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error enviando el parte.");
    } finally {
      setSending(false);
    }
  };

  // --------------------------
  // Helpers
  // --------------------------
  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const calcularDuracionMs = (inicio, fin) => {
    const a = parseDateSafe(inicio)?.getTime();
    const b = parseDateSafe(fin)?.getTime();
    if (!a || !b) return 0;
    return b - a;
  };

  const calcularDuracionTxt = (ms) => {
    if (!ms || isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  const formatearDuracion = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}min`;
  };

  // Normalizador búsqueda (acentos/mayúsculas)
  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  // --------------------------
  // Carga
  // --------------------------
  useEffect(() => {
    const fetchDatos = async () => {
      setCargando(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [resT, resCtes, resCtrs, resOts, resUsers] = await Promise.all([
          fetch(`${Global.url}ticajes/finalizados/mios`, { headers }),
          fetch(`${Global.url}clientes`, { headers }),
          fetch(`${Global.url}centros/todos`, { headers }),
          fetch(`${Global.url}ordenes`, { headers }),
          fetch(`${Global.url}usuarios`, { headers }),
        ]);

        if (!resT.ok || !resCtes.ok || !resCtrs.ok || !resOts.ok || !resUsers.ok) {
          toast.error("Error al obtener datos.");
          return;
        }

        const [dataT, dataCtes, dataCtrs, dataOts, dataUsers] = await Promise.all([
          resT.json(),
          resCtes.json(),
          resCtrs.json(),
          resOts.json(),
          resUsers.json(),
        ]);

        setClientes(Array.isArray(dataCtes) ? dataCtes : []);
        setCentros(Array.isArray(dataCtrs) ? dataCtrs : []);
        setOrdenes(Array.isArray(dataOts) ? dataOts : []);
        setUsuarios(Array.isArray(dataUsers) ? dataUsers : []);

        const normalizados = (Array.isArray(dataT) ? dataT : []).map((t) => ({
          ...t,
          duracionMs: calcularDuracionMs(t.inicio, t.fin),
        }));

        setTicajes(normalizados);
      } catch (error) {
        console.error("Error en la conexión:", error);
        toast.error("No se pudo conectar con el servidor.");
      } finally {
        setCargando(false);
      }
    };

    fetchDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------
  // Maps (joins en memoria)
  // --------------------------
  const mapClientes = useMemo(() => {
    const m = {};
    clientes.forEach((c) => (m[String(c.id)] = c.nombre));
    return m;
  }, [clientes]);

  const mapCentros = useMemo(() => {
    const m = {};
    centros.forEach((c) => (m[String(c.id)] = c.nombre));
    return m;
  }, [centros]);

  const mapOrdenes = useMemo(() => {
    const m = {};
    ordenes.forEach((o) => (m[String(o.id)] = o.titulo || `OT ${o.id}`));
    return m;
  }, [ordenes]);

  const mapUsuarios = useMemo(() => {
    const m = {};
    usuarios.forEach((u) => {
      const full = [u.nombre, u.apellidos].filter(Boolean).join(" ").trim();
      m[String(u.id)] = full || u.nombre || `Usuario ${u.id}`;
    });
    return m;
  }, [usuarios]);

  // --------------------------
  // Opciones filtros (Autocomplete)
  // --------------------------
  const clientesOrdenados = useMemo(() => {
    return clientes.slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes]);

  const ordenesOrdenadas = useMemo(() => {
    return ordenes.slice().sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""));
  }, [ordenes]);

  const clienteSeleccionado =
    clientesOrdenados.find((c) => String(c.id) === String(filtroCliente)) || null;

  const ordenSeleccionada =
    ordenesOrdenadas.find((o) => String(o.id) === String(filtroOrden)) || null;

  // --------------------------
  // Filtrado
  // --------------------------
  const q = normaliza(busquedaTexto);

  const ticajesFiltrados = useMemo(() => {
    return (ticajes || []).filter((t) => {
      const coincideUsuario = String(t.usuario_id) === String(auth?.id);

      const fechaTicaje = parseDateSafe(t.inicio);
      if (!fechaTicaje) return false;

      const anio = String(fechaTicaje.getFullYear());
      const mes = String(fechaTicaje.getMonth() + 1).padStart(2, "0");

      const coincideCliente = !filtroCliente || String(t.cliente_id ?? "") === String(filtroCliente);
      const coincideOrden = !filtroOrden || String(t.orden_id ?? "") === String(filtroOrden);

      const desdeValida = !fechaDesde || fechaTicaje >= new Date(fechaDesde);
      const hastaValida =
        !fechaHasta ||
        fechaTicaje <
          new Date(new Date(fechaHasta).setDate(new Date(fechaHasta).getDate() + 1));

      const coincideAnio = !filtroAnio || anio === filtroAnio;
      const coincideMes = !filtroMes || mes === filtroMes;

      const clienteNombre = mapClientes[String(t.cliente_id)] || "";
      const centroNombre = mapCentros[String(t.centro_id)] || "";
      const ordenNombre = mapOrdenes[String(t.orden_id)] || "";
      const modificadoPor = mapUsuarios[String(t.creado_por_admin_id)] || "";
      const comentarios = t.comentarios || "";

      const coincideTexto =
        !q ||
        normaliza(clienteNombre).includes(q) ||
        normaliza(centroNombre).includes(q) ||
        normaliza(ordenNombre).includes(q) ||
        normaliza(modificadoPor).includes(q) ||
        normaliza(comentarios).includes(q);

      return (
        coincideUsuario &&
        coincideCliente &&
        coincideOrden &&
        desdeValida &&
        hastaValida &&
        coincideAnio &&
        coincideMes &&
        coincideTexto
      );
    });
  }, [
    ticajes,
    auth?.id,
    filtroCliente,
    filtroOrden,
    fechaDesde,
    fechaHasta,
    filtroMes,
    filtroAnio,
    q,
    mapClientes,
    mapCentros,
    mapOrdenes,
    mapUsuarios,
  ]);

  useEffect(() => {
    setPage(0);
  }, [filtroCliente, filtroOrden, fechaDesde, fechaHasta, filtroMes, filtroAnio, busquedaTexto]);

  // --------------------------
  // Ordenación controlada
  // --------------------------
  const ticajesOrdenados = useMemo(() => {
    const arr = [...ticajesFiltrados];

    arr.sort((a, b) => {
      let valorA = a[orden.campo];
      let valorB = b[orden.campo];

      if (orden.campo === "cliente") {
        valorA = mapClientes[String(a.cliente_id)] || "";
        valorB = mapClientes[String(b.cliente_id)] || "";
      }

      if (orden.campo === "centro") {
        valorA = mapCentros[String(a.centro_id)] || "";
        valorB = mapCentros[String(b.centro_id)] || "";
      }

      if (orden.campo === "orden") {
        valorA = mapOrdenes[String(a.orden_id)] || "";
        valorB = mapOrdenes[String(b.orden_id)] || "";
      }

      if (orden.campo === "modificado_por") {
        valorA = mapUsuarios[String(a.creado_por_admin_id)] || "";
        valorB = mapUsuarios[String(b.creado_por_admin_id)] || "";
      }

      if (orden.campo === "inicio" || orden.campo === "fin") {
        valorA = parseDateSafe(valorA)?.getTime() ?? 0;
        valorB = parseDateSafe(valorB)?.getTime() ?? 0;
      }

      if (orden.campo === "duracionMs") {
        valorA = a.duracionMs ?? 0;
        valorB = b.duracionMs ?? 0;
      }

      if (typeof valorA === "string") valorA = valorA.toLowerCase();
      if (typeof valorB === "string") valorB = valorB.toLowerCase();

      valorA = valorA ?? "";
      valorB = valorB ?? "";

      if (valorA < valorB) return orden.ascendente ? -1 : 1;
      if (valorA > valorB) return orden.ascendente ? 1 : -1;
      return 0;
    });

    return arr;
  }, [ticajesFiltrados, orden, mapClientes, mapCentros, mapOrdenes, mapUsuarios]);

  // Total trabajado
  const totalMs = useMemo(
    () => ticajesFiltrados.reduce((acc, t) => acc + (t.duracionMs || 0), 0),
    [ticajesFiltrados]
  );

  // --------------------------
  // DataGrid rows/cols
  // --------------------------
  const rows = useMemo(() => {
    return ticajesOrdenados.map((t) => ({
      ...t,
      id: t.id,
      cliente_nombre: mapClientes[String(t.cliente_id)] || `Cliente ${t.cliente_id}`,
      centro_nombre: mapCentros[String(t.centro_id)] || `Centro ${t.centro_id}`,
      orden_nombre: t.orden_id ? (mapOrdenes[String(t.orden_id)] || `OT ${t.orden_id}`) : "-",
      inicio_txt: fmtDate(t.inicio),
      fin_txt: fmtDate(t.fin),
      duracion_txt: calcularDuracionTxt(t.duracionMs),
      comentarios_txt: (t.comentarios || "Sin comentarios").toString(),
      modificado_txt: t.creado_por_admin_id ? (mapUsuarios[String(t.creado_por_admin_id)] || "-") : "-",
    }));
  }, [ticajesOrdenados, mapClientes, mapCentros, mapOrdenes, mapUsuarios]);

  const columns = useMemo(() => {
    const baseColumns = [
      { field: "orden_nombre", headerName: "OT", flex: 1, minWidth: 240, sortable: false },
      { field: "cliente_nombre", headerName: "Cliente", flex: 1, minWidth: 220, sortable: false },
      { field: "centro_nombre", headerName: "Centro", flex: 1, minWidth: 240, sortable: false },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 170, sortable: false },
      { field: "fin_txt", headerName: "Fin", minWidth: 170, sortable: false },
      { field: "duracion_txt", headerName: "Duración", minWidth: 130, sortable: false },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260, sortable: false },
      { field: "modificado_txt", headerName: "Modificado por", minWidth: 200, sortable: false },
    ];

    if (Global.partes_trabajo !== "si") return baseColumns;

    const colParte = {
      field: "parte",
      headerName: "Parte",
      minWidth: 140,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() =>
            navigate(`/app/misIntervenciones/${params.row.id}/parte`, {
              state: {
                ot: params.row.orden_nombre,
                cliente: params.row.cliente_nombre,
                centro: params.row.centro_nombre,
                fecha: params.row.inicio_txt,
              },
            })
          }
        >
          Parte
        </Button>
      ),
    };

    const colEnviar = {
      field: "enviar",
      headerName: "Enviar",
      minWidth: 160,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => openSendModal(params.row)}
        >
          Enviar
        </Button>
      ),
    };

    return [...baseColumns, colParte, colEnviar];
  }, [navigate, openSendModal]);

  // Orden clickable desde header
  const onHeaderClick = (field) => {
    const map = {
      orden_nombre: "orden",
      cliente_nombre: "cliente",
      centro_nombre: "centro",
      inicio_txt: "inicio",
      fin_txt: "fin",
      duracion_txt: "duracionMs",
      comentarios_txt: "comentarios",
      modificado_txt: "modificado_por",
    };

    const campoOrden = map[field] || field;
    setOrden((prev) => ({
      campo: campoOrden,
      ascendente: prev.campo === campoOrden ? !prev.ascendente : true,
    }));
  };

  const limpiarFiltros = () => {
    setFiltroCliente("");
    setFiltroOrden("");
    setFechaDesde("");
    setFechaHasta("");
    setFiltroMes("");
    setFiltroAnio("");
    setBusquedaTexto("");
    toast.info("Filtros limpiados.");
  };

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Mis Intervenciones Finalizadas
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "end",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(12, 1fr)",
              },
            }}
          >
            {/* OT */}
            <Autocomplete
              size="small"
              options={ordenesOrdenadas}
              getOptionLabel={(o) => o?.titulo || (o?.id ? `OT ${o.id}` : "")}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={ordenSeleccionada}
              onChange={(_, newValue) => setFiltroOrden(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="OT" placeholder="Escribe para buscar…" fullWidth />
              )}
              clearOnEscape
              sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 4" } }}
            />

            {/* Cliente */}
            <Autocomplete
              size="small"
              options={clientesOrdenados}
              getOptionLabel={(c) => c?.nombre || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={clienteSeleccionado}
              onChange={(_, newValue) => setFiltroCliente(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="Cliente" placeholder="Escribe para buscar…" fullWidth />
              )}
              clearOnEscape
              sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 4" } }}
            />

            {/* Desde */}
            <TextField
              size="small"
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            />

            {/* Hasta */}
            <TextField
              size="small"
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            />

            {/* Año */}
            <FormControl
              size="small"
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            >
              <InputLabel>Año</InputLabel>
              <Select label="Año" value={filtroAnio} onChange={(e) => setFiltroAnio(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 16 }, (_, i) => {
                  const year = 2025 + i;
                  return (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Mes */}
            <FormControl
              size="small"
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            >
              <InputLabel>Mes</InputLabel>
              <Select label="Mes" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                    {new Date(0, i).toLocaleString("es-ES", { month: "long" })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Buscar */}
            <TextField
              size="small"
              label="Buscar"
              value={busquedaTexto}
              onChange={(e) => setBusquedaTexto(e.target.value)}
              placeholder="Cliente, centro, OT, comentarios o modificado por"
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 6" } }}
            />

            {/* Limpiar */}
            <Button
              variant="contained"
              onClick={limpiarFiltros}
              sx={{
                height: 40,
                gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 2" },
                justifySelf: { md: "end" },
              }}
            >
              Limpiar filtros
            </Button>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 620, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={cargando}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            onColumnHeaderClick={(params) => onHeaderClick(params.field)}
            sx={{
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
              "& .MuiDataGrid-cellContent": { width: "100%" },
              "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
            }}
          />
        </Paper>

        {/* Total */}
        <Paper sx={{ p: 2 }}>
          <Typography sx={{ textAlign: "right" }}>
            <strong>Total trabajado en este listado:</strong> {formatearDuracion(totalMs)}
          </Typography>
        </Paper>
      </Stack>

      {/* Modal envío email */}
      <Dialog open={modalOpen} onClose={closeSendModal} fullWidth maxWidth="sm">
        <DialogTitle>Enviar parte por email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Se generará un PDF con el parte y se enviará como adjunto.
            </Alert>

            <TextField
              label="Email del cliente"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              fullWidth
              autoFocus
              placeholder="cliente@empresa.com"
              error={Boolean(emailTo) && !isEmailValid(emailTo)}
              helperText={
                emailTo && !isEmailValid(emailTo) ? "Introduce un email válido." : " "
              }
            />

            {selectedTicaje ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                OT: {selectedTicaje?.orden_nombre || "-"} · Cliente:{" "}
                {selectedTicaje?.cliente_nombre || "-"} · Centro:{" "}
                {selectedTicaje?.centro_nombre || "-"}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendModal} disabled={sending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={enviarParteEmail}
            disabled={sending || !isEmailValid(emailTo) || !selectedTicaje?.id}
          >
            {sending ? "Enviando…" : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
